import { z } from 'zod'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { TerritoryRepository } from '../../application/TerritoryRepository'
import type { TerritoryPoint, TerritoryResult, TerritoryRoute } from '../../domain/territory'

// Les Soumissions encore en jeu et celles déjà perdues sont toutes deux utiles :
// les premières se gagnent, les secondes disent quel concurrent tient la rue.
const pendingStatuses = ['envoyee'] as const
const lostStatuses = ['refusee', 'expiree'] as const

const clientColumns =
  'id,numero,prenom,nom,entreprise,telephone,adresse,ville,latitude,longitude,created_at,' +
  // Le prix affiché est celui de l'engagement en cours, pas une moyenne.
  'contrats:contracts(prix,statut)'
const quoteColumns =
  'id,numero,statut,total,competiteur,' +
  'client:clients(id,numero,prenom,nom,entreprise,telephone,adresse,ville,latitude,longitude),' +
  'lead:leads(id,numero,prenom,nom,telephone,adresse,ville,latitude,longitude)'

// L'ordre de passage fait partie de la Route : un tracé dans le désordre
// décrirait un trajet qui n'existe pas.
const routeColumns =
  'id,nom,couleur,secteur,' +
  'arrets:route_contracts(ordre,contrat:contracts(numero,prix,client:clients(prenom,nom,entreprise,adresse,latitude,longitude)))'

const positionSchema = z.object({
  latitude: z.coerce.number().min(-90).max(90).nullable(),
  longitude: z.coerce.number().min(-180).max(180).nullable(),
})

const clientRowSchema = positionSchema.extend({
  id: z.uuid(),
  numero: z.string().nullable(),
  prenom: z.string().nullable(),
  nom: z.string().nullable(),
  entreprise: z.string().nullable(),
  telephone: z.string().nullable().optional(),
  adresse: z.string().nullable(),
  ville: z.string().nullable(),
  created_at: z.string().optional(),
  contrats: z
    .array(z.object({ prix: z.coerce.number().nullable(), statut: z.string() }))
    .nullable()
    .optional(),
})

const quoteRowSchema = z.object({
  id: z.uuid(),
  numero: z.string().nullable(),
  statut: z.string(),
  total: z.coerce.number().nullable(),
  competiteur: z.string().nullable(),
  client: clientRowSchema.nullable().optional(),
  lead: positionSchema
    .extend({
      id: z.uuid(),
      numero: z.string().nullable(),
      prenom: z.string().nullable(),
      nom: z.string().nullable(),
      telephone: z.string().nullable().optional(),
      adresse: z.string().nullable(),
      ville: z.string().nullable(),
    })
    .nullable()
    .optional(),
})

function displayName(parts: {
  entreprise?: string | null
  prenom: string | null
  nom: string | null
}): string {
  const company = parts.entreprise?.trim()
  if (company) return company
  const person = [parts.prenom, parts.nom].filter(Boolean).join(' ').trim()
  return person || 'Sans nom'
}

function optional(value: string | null | undefined): string | undefined {
  const normalized = value?.trim()
  // Une chaîne vide n'est pas une information : elle vaut absence.
  return normalized === undefined || normalized === '' ? undefined : normalized
}

const routeRowSchema = z.object({
  id: z.uuid(),
  nom: z.string().nullable(),
  couleur: z.string().nullable(),
  secteur: z.string().nullable().optional(),
  arrets: z
    .array(
      z.object({
        ordre: z.number().int().nullable(),
        contrat: z
          .object({
            numero: z.string().nullable(),
            prix: z.coerce.number().nullable(),
            client: z
              .object({
                prenom: z.string().nullable(),
                nom: z.string().nullable(),
                entreprise: z.string().nullable(),
                adresse: z.string().nullable(),
                latitude: z.coerce.number().nullable(),
                longitude: z.coerce.number().nullable(),
              })
              .nullable(),
          })
          .nullable(),
      }),
    )
    .nullable()
    .optional(),
})

export function createSupabaseTerritoryRepository(client: SupabaseClient): TerritoryRepository {
  return {
    async load(): Promise<TerritoryResult> {
      const [clients, quotes, routes] = await Promise.all([
        client.from('clients').select(clientColumns).is('deleted_at', null),
        client
          .from('quotes')
          .select(quoteColumns)
          .is('deleted_at', null)
          .in('statut', [...pendingStatuses, ...lostStatuses]),
        client.from('routes').select(routeColumns).is('deleted_at', null),
      ])
      if (clients.error) throw new Error(clients.error.message)
      if (quotes.error) throw new Error(quotes.error.message)
      if (routes.error) throw new Error(routes.error.message)

      const points: TerritoryPoint[] = []
      let unlocated = 0

      for (const raw of clients.data) {
        const parsed = clientRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        if (row.latitude === null || row.longitude === null) {
          unlocated += 1
          continue
        }
        // Le contrat actif fait foi; à défaut, aucun prix n'est affiché plutôt
        // qu'un montant repris d'un engagement terminé.
        const activeContract = row.contrats?.find((contract) => contract.statut === 'actif')
        const address = optional(row.adresse)
        const city = optional(row.ville)
        const phone = optional(row.telephone)
        points.push({
          id: `client:${row.id}`,
          kind: 'CLIENT',
          ...(row.numero ? { reference: row.numero } : {}),
          label: displayName(row),
          ...(address ? { address } : {}),
          ...(city ? { city } : {}),
          latitude: row.latitude,
          longitude: row.longitude,
          ...(activeContract?.prix != null ? { amountCents: Math.round(activeContract.prix * 100) } : {}),
          ...(phone ? { phone } : {}),
          ...(row.created_at ? { clientSince: new Date(row.created_at).getFullYear() } : {}),
          href: row.numero ? `/clients/${row.numero}` : '/clients',
        })
      }

      const competitorTally = new Map<string, number>()
      for (const raw of quotes.data) {
        const parsed = quoteRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        const competitor = optional(row.competiteur)
        if (competitor) competitorTally.set(competitor, (competitorTally.get(competitor) ?? 0) + 1)

        // La position d'une Soumission est celle de son destinataire : le Client
        // quand il existe, sinon le Lead d'origine.
        const origin = row.client ?? row.lead
        if (origin?.latitude == null || origin.longitude === null) {
          unlocated += 1
          continue
        }
        const lost = (lostStatuses as readonly string[]).includes(row.statut)
        const address = optional(origin.adresse)
        const city = optional(origin.ville)
        const phone = optional(origin.telephone)
        // Un Lead n'a pas d'entreprise : seul un Client en porte une.
        const company = row.client?.entreprise ?? null
        points.push({
          id: `quote:${row.id}`,
          kind: lost ? 'LOST_QUOTE' : 'PENDING_QUOTE',
          ...(row.numero ? { reference: row.numero } : {}),
          label: displayName({ prenom: origin.prenom, nom: origin.nom, entreprise: company }),
          ...(address ? { address } : {}),
          ...(city ? { city } : {}),
          latitude: origin.latitude,
          longitude: origin.longitude,
          // `total` est en dollars dans le schéma legacy; la carte parle en cents.
          ...(row.total !== null ? { amountCents: Math.round(row.total * 100) } : {}),
          ...(competitor ? { competitor } : {}),
          ...(phone ? { phone } : {}),
          href: row.numero ? `/quotes/${row.numero}` : '/quotes',
        })
      }

      const mappedRoutes: TerritoryRoute[] = []
      for (const raw of routes.data) {
        const parsed = routeRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        const stops = (row.arrets ?? [])
          .slice()
          .sort((left, right) => (left.ordre ?? 0) - (right.ordre ?? 0))
          .flatMap((stop) => {
            const stopClient = stop.contrat?.client
            if (stopClient?.latitude == null || stopClient.longitude === null) return []
            return [
              {
                latitude: stopClient.latitude,
                longitude: stopClient.longitude,
                label: optional(stopClient.adresse) ?? displayName(stopClient),
              },
            ]
          })
        // Une Route dont aucune adresse n'est positionnée ne se trace pas.
        if (stops.length < 2) continue
        const sector = optional(row.secteur)
        // Valeur de la Route : la somme des engagements qu'elle dessert.
        const totalCents = (row.arrets ?? []).reduce(
          (sum, stop) => sum + Math.round((stop.contrat?.prix ?? 0) * 100),
          0,
        )
        mappedRoutes.push({
          id: row.id,
          name: optional(row.nom) ?? 'Route sans nom',
          ...(sector ? { sector } : {}),
          contractsTotalCents: totalCents,
          color: optional(row.couleur) ?? '#14304f',
          stops,
        })
      }

      return {
        points,
        routes: mappedRoutes,
        summary: {
          clients: points.filter((point) => point.kind === 'CLIENT').length,
          pendingQuotes: points.filter((point) => point.kind === 'PENDING_QUOTE').length,
          lostQuotes: points.filter((point) => point.kind === 'LOST_QUOTE').length,
          unlocated,
          competitors: [...competitorTally.entries()]
            .map(([name, count]) => ({ name, count }))
            .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name, 'fr-CA')),
        },
        meta: { source: 'supabase', retrievedAt: new Date().toISOString() },
      }
    },
  }
}
